import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import * as graphql from 'generated/graphql';

import {
	ActivityIcon,
	ConversationIcon,
	DirectionsIcon,
	DialogueIcon,
	DraftsIcon,
	GearIcon,
	GridIcon,
	HomeIcon,
	LibraryIcon,
	NewBadgeIcon,
	SuitcaseIcon,
	TargetIcon,
} from '@treams/treams-icons';
import { Sidebar } from '@treams/treams-ui';

import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useViewerRoles from '../packages/kakapo-ui-application/hooks/useUserRoles';

import Drawer from './Drawer';

export default function GeneralSidebar(props: { visible?: boolean; onClose?: () => void }) {
	const { t } = useTranslation();

	const hasGoals = useFeature(graphql.FeatureName.Goals);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasLearning = useFeature(graphql.FeatureName.Learning);
	const hasNotes = useFeature(graphql.FeatureName.Notes);
	const hasTalentReviews = useFeature(graphql.FeatureName.TalentReviews);
	const hasFeedbackCompetencies = useFeature(graphql.FeatureName.ModuleSkills);
	const hasBeamer = useFeature(graphql.FeatureName.ProductBeamer);
	const usesHelicopter = useFeature(graphql.FeatureName.Helicopter);
	const usesDevelopmentPlan = useFeature(graphql.FeatureName.DevelopmentPlan);
	const { isWorkspaceSuperuser, isGroupManager } = useViewerRoles();

	return (
		<Drawer position={props.visible ? 'active' : 'left'} onClose={props.onClose}>
			<Sidebar.Item
				icon={<HomeIcon />}
				title={t('sidebar.startpage')}
				path={['/', '/inbox/manager', '/journey']}
				exact
			/>
			{hasGoals && <Sidebar.Item icon={<TargetIcon />} title={t('sidebar.goals')} path="/goals" />}
			{hasFeedback && (
				<Sidebar.Item icon={<DialogueIcon />} title={t('sidebar.feedback')} path="/feedback" />
			)}
			{hasFeedbackCompetencies && (
				<Sidebar.Item
					icon={<SuitcaseIcon />}
					title={t('sidebar.competencies')}
					path="/competencies"
				/>
			)}
			{hasConversations && (
				<Sidebar.Item
					icon={<ConversationIcon />}
					title={t('sidebar.conversations')}
					path="/conversations"
				/>
			)}
			{!usesHelicopter && hasTalentReviews && (isWorkspaceSuperuser || isGroupManager) && (
				<Sidebar.Item
					icon={<GridIcon />}
					title={t('sidebar.talentReview')}
					path="/talent-reviews"
				/>
			)}
			{hasNotes && <Sidebar.Item icon={<DraftsIcon />} title={t('sidebar.notes')} path="/notes" />}
			{usesDevelopmentPlan && (
				<Sidebar.Item
					icon={<DirectionsIcon />}
					title={t('sidebar.developmentPlan')}
					path="/development-plan"
				/>
			)}
			{hasLearning && (
				<Sidebar.Item icon={<LibraryIcon />} title={t('sidebar.learning')} path="/learning" />
			)}
			<Sidebar.Spacing />

			<Box flexGrow={1} flexShrink={1} />
			<Sidebar.Item
				icon={<ActivityIcon />}
				title={t('sidebar.analytics')}
				path={
					isWorkspaceSuperuser
						? '/analytics/v2'
						: isGroupManager
						? '/analytics/department'
						: '/analytics/employee'
				}
			/>

			{(isWorkspaceSuperuser || hasBeamer) && (
				<>
					{isWorkspaceSuperuser ? (
						<Sidebar.Item icon={<GearIcon />} title={t('sidebar.manage')} path="/settings" />
					) : (
						<Sidebar.Spacing />
					)}
					{hasBeamer && (
						<div className="beamerButton">
							<Sidebar.Item
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								onClick={() => (window as any).Beamer?.show()}
								icon={<NewBadgeIcon />}
								title={t('settings.general.changeLog')}
								target="_blank"
							/>
						</div>
					)}
				</>
			)}
		</Drawer>
	);
}
